import { loadTranslation } from "src/common/api/apiTranslation";
import { ZUid } from "src/types/zUid";
import { AttrTypeName } from "src/types/AttrType";
import { EdAttribute, attr2edit } from "../../Obj2Tab/EdAttribute";
import { saveAttrToOwner } from "../../Obj2Tab/utils/groupAndAttrSave";
import { onAttrSaveMap } from "../../Obj2Tab/utils/onAttrSaveMap";
import { transformEdAttVals4Save } from "../../Obj2Tab/utils/transforms";
import { EdObjStates } from "../../Obj2Tab/EdObject";
import { createPermissions } from "../../Obj2Tab/roles/createPermissions";
import { loadAttributePermissions } from "../../Obj2Tab/roles/rolesApi";
import { getAttrTypeIcon } from "../../Obj2Tab/utils/typeIcons";
import {
  deleteAttribute,
  deleteAttributeFromValue,
  loadRestrictions,
} from "../../objectsApi";
import { AttributeNode, attributeNodeId, unsavedId } from "../model/node";
import { attributeActionsOf } from "../model/permissions";
import { renderNodeTitle } from "./title";
import { saveOwnerOf } from "./saveOwner";
import {
  KindContext,
  KindHandler,
  RemovableKindHandler,
  SavableKindHandler,
} from "./types";

/**
 * Атрибут — лист дерева: метода `children` у обработчика нет, и это видно
 * системе типов, а не выясняется в рантайме.
 */
export const attributeKind: KindHandler<AttributeNode> &
  RemovableKindHandler<AttributeNode> &
  SavableKindHandler<AttributeNode> = {
  title: (node, actions) =>
    renderNodeTitle({
      name: node.data.name,
      id: node.data.id,
      displayId: node.data.displayId,
      disabled: !actions.update,
      placeholder: "Новый атрибут",
    }),

  icon: (node) => getAttrTypeIcon(node.data),

  // Списка прав нет ни у черновика, ни у атрибута, по которому сервер его не
  // прислал: и то и другое означает «своих ограничений нет», а ограничения
  // владельца наложит вызывающая сторона
  ownActions: (node) => attributeActionsOf(node.data),

  formData: async (node, ctx) => {
    const owner = ctx.ownerObject(node.id);
    if (!owner) throw Error(`Не найден объект для атрибута ${node.data.name}`);
    const attrId = node.data.id;
    const isNew = attrId === unsavedId;
    // Состояния объекта уже загружены вместе с ним: своего запроса не нужно
    const states = ctx.states(owner.data.id) ?? [];
    const restrictions = isNew ? null : await loadRestrictions(attrId);
    // Права нового атрибута строятся из справочника, существующего - читаются
    const permissionsOf = async (stateId: ZUid) =>
      isNew
        ? createPermissions(ctx.attrTypeActions, ctx.roleGroups)
        : loadAttributePermissions(attrId, stateId);
    const pairs = await Promise.all(
      states.map(
        async ({ id }) => [String(id), await permissionsOf(id)] as const,
      ),
    );
    const rolesMap: EdObjStates = Object.fromEntries(pairs);
    const { translations } = await loadTranslation(node.data.name);
    return attr2edit(node.data, rolesMap, restrictions, translations);
  },

  removeMessage: () => "Удалить атрибут?",

  /**
   * Атрибут, показанный внутри значения справочника, принадлежит не объекту,
   * а этому значению: его отвязывают, а не удаляют совсем.
   */
  remove: async (node, ctx: KindContext) => {
    const owner = ctx.node(node.ownerId);
    if (owner?.kind === "value") {
      const group = ctx.node(owner.ownerId);
      if (group?.kind !== "group") {
        throw Error(`Не найдена группа значения ${owner.data.name}`);
      }
      await deleteAttributeFromValue(
        group.data.id,
        owner.data.id,
        node.data.id,
      );
      return;
    }
    if (node.data.id !== unsavedId) await deleteAttribute(node.data.id);
  },

  save: async (node, values, ctx) => {
    const owner = ctx.ownerObject(node.id);
    if (!owner) throw Error(`Не найден объект для атрибута ${node.data.name}`);
    const prepared = await transformEdAttVals4Save(
      values as EdAttribute,
      owner.data.id,
    );
    const saved = await saveAttrToOwner(
      saveOwnerOf(node, ctx),
      node.data,
      prepared,
    );
    // Ссылочный атрибут дополнительно правит того, на кого ссылается
    const typeName = ctx.attrTypesDict[node.data.valueType] as AttrTypeName;
    await onAttrSaveMap[typeName]?.(node.data, saved);
    return {
      objectId: owner.data.id,
      selectId: attributeNodeId(saved.id, node.ownerId),
    };
  },
};
